import { useDispatch } from "react-redux";
import { Button } from "@financeable-com-au/financeable-ui";
// eslint-disable-next-line import/no-unresolved
import { COPY_BUTTON_COPY } from "@/constants";
// eslint-disable-next-line import/no-unresolved
import { openSnackbar } from "@/store/slices/snackbarSlice";
import type { AppDispatch } from "@/store/store";

interface CopyButtonProps {
  /** Copied as plain text: what a paste into a field or a plain-text editor gets. */
  text: string;
  /** The same content as HTML, copied alongside the text, so a paste into a document keeps its formatting and links. */
  html?: string;
  /** Says what the button copies, for a screen reader, as its label only says "Copy". */
  "aria-label"?: string;
}

/** Plain text alone needs no ClipboardItem, which some older browsers lack. */
const writeToClipboard = ({ text, html }: Pick<CopyButtonProps, "text" | "html">) =>
  html
    ? navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ])
    : navigator.clipboard.writeText(text);

/**
 * Copies to the clipboard and says in a snackbar whether it worked. Given HTML as well as text, it copies both, and
 * whatever it is pasted into takes the richest format it can. Disabled while there is nothing to copy.
 */
export const CopyButton = ({ text, html, "aria-label": ariaLabel }: CopyButtonProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleCopy = async () => {
    try {
      await writeToClipboard({ text, html });
      dispatch(openSnackbar({ message: COPY_BUTTON_COPY.copied, intent: "success" }));
    } catch {
      // The clipboard can be blocked, or missing over http or in an old browser; the text can still be selected.
      dispatch(openSnackbar({ message: COPY_BUTTON_COPY.copyFailed, intent: "danger" }));
    }
  };

  return (
    <Button variant="secondary" size="sm" disabled={!text} aria-label={ariaLabel} onClick={handleCopy}>
      {COPY_BUTTON_COPY.copy}
    </Button>
  );
};
